import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import DLOTooltip from '../../../../../modules/dlo/components/DLOTooltip';
import TableSkeleton from '../../../components/TableSkeleton';
import ExportButton from '../components/ExportButton';
import styles from '../OrderRulesReport.styles';
import { updateOrderRuleCheckReview } from '../orderRulesApi';
import { RootState } from '../../../../../store';
import { formatNameFromEmail } from '../../../../ActionPlan/utils/actionPlanTransformers';

// Only these roles may confirm a review, and only for build locations the user has access to.
const REVIEW_CONFIRM_ROLE_IDS = new Set([1, 2, 3, 4, 5, 15]);

interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  minWidth?: number;
}

interface OrderRuleCheckTabProps {
  loading: boolean;
  checkBuildRows: Array<Record<string, any>>;
  /** Server-side sort state — column key + direction currently applied via the API. */
  sort: { col: string | null; dir: 'asc' | 'desc' | null };
  /** Fires the sortBy/sortOrder API call. */
  onSort: (colKey: string) => void;
  /** Controlled — defaults to false; toggling refetches the API with `showNonEligibleSkus` set accordingly. */
  showNonEligibleSkus: boolean;
  onShowNonEligibleSkusChange: (value: boolean) => void;
  /** Controlled — defaults to false; toggling refetches the API with `showReviewed` set accordingly. */
  showOnlyReviewed: boolean;
  onShowOnlyReviewedChange: (value: boolean) => void;
  /** Controlled — mutually exclusive with showNonEligibleSkus; sent to the API as showNonInvoicedSkus only when enabled. */
  showOnlyNonInvoicedSkus: boolean;
  onShowOnlyNonInvoicedSkusChange: (value: boolean) => void;
  /** Pagination bar, rendered directly under the table. */
  pagination?: React.ReactNode;
  /** Called after a successful review toggle so the parent can re-fetch latest table data. */
  onReviewToggleSuccess?: () => void | Promise<void>;
  /** Triggers the export API for this tab. */
  onExport: () => Promise<void>;
}

const getBuildRowKey = (row: Record<string, any>): string =>
  [
    String(row.location ?? ''),
    String(row.materialNumber ?? ''),
    String(row.packageName ?? ''),
    String(row.description ?? ''),
    String(row.buildPallet ?? ''),
    String(row.packSpec ?? ''),
    String(row.orderRule ?? ''),
  ].join('|');

const isReviewRow = (row: Record<string, any>): boolean =>
  String(row.flag ?? '').trim().toLowerCase() === 'review' || Boolean(row.reviewFlag);

/** Formats an ISO date string as MM/DD/YY, e.g. "08/12/26". */
const formatReviewedDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

// Consolidated Build Location + Cross Dock table: cross dock rows for the same
// material render as expandable child rows under their serving build location,
// matching the expand/collapse pattern used on the Max Potential tab. The
// standalone Crossdocks table is retired.
const checkColumns: TableColumn[] = [
  { key: 'location', label: 'Build Location', minWidth: 190, align: 'left' },
  { key: 'materialNumber', label: 'Material No.', minWidth: 130, align: 'right' },
  { key: 'packageName', label: 'Package', minWidth: 170, align: 'left' },
  { key: 'description', label: 'Material Description', minWidth: 260, align: 'left' },
  { key: 'buildPallet', label: 'Build Pallet', minWidth: 110, align: 'right' },
  { key: 'packSpec', label: 'Pack Spec', minWidth: 100, align: 'right' },
  { key: 'orderRule', label: 'Order Rule', minWidth: 100, align: 'right' },
  { key: 'flag', label: 'Flag', minWidth: 230, align: 'left' },
  { key: 'reviewAction', label: 'Site Confirmation', minWidth: 180, align: 'center' },
];

const getColumnSizingStyle = (column: TableColumn) => ({
  width: column.minWidth ?? 120,
  minWidth: column.minWidth ?? 120,
  maxWidth: column.minWidth ?? 120,
});

const checkHeaderTooltips: Record<string, string> = {
  buildPallet: 'Cases per layer from the Ortec build pallet.',
  packSpec: 'Cases per layer from the SAP pack spec.',
  orderRule: 'Cases per layer from the order rule product table. Shows N/A when the SKU is non-eligible.',
  flag: 'For build locations, the review flag is raised when Build Pallet, Pack Spec, and Order Rule cases per layer do not align. For crossdock locations, the review flag is raised when the Order Rule cases per layer does not align with the build location.',
  reviewAction:
    'For rows flagged for review, the site confirms the Build Pallet, Pack Spec, and Order Rule values have been reviewed and accepted. Reviewed rows persist for 3 periods or until a value changes, whichever happens first.',
};

/** Fixed row height and max visible rows before the body scrolls vertically — keeps the sticky header pattern consistent across tabs. */
const ROW_HEIGHT = 44;
const MAX_VISIBLE_ROWS = 12;

const OrderRuleCheckTab: React.FC<OrderRuleCheckTabProps> = ({
  loading,
  checkBuildRows,
  sort: checkSort,
  onSort: handleCheckSort,
  showNonEligibleSkus,
  onShowNonEligibleSkusChange,
  showOnlyReviewed,
  onShowOnlyReviewedChange,
  showOnlyNonInvoicedSkus,
  onShowOnlyNonInvoicedSkusChange,
  pagination,
  onReviewToggleSuccess,
  onExport,
}) => {
  const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null);
  const [acceptedBuildRowMap, setAcceptedBuildRowMap] = useState<Record<string, boolean>>({});
  const [reviewMetaMap, setReviewMetaMap] = useState<Record<string, { reviewedBy?: string; reviewedTime?: string }>>({});
  const [pendingBuildRowMap, setPendingBuildRowMap] = useState<Record<string, boolean>>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<Record<string, boolean>>({});

  const roleId = useSelector((state: RootState) => state.appData?.userInfo?.roleId);
  const accessibleLocations = useSelector((state: RootState) => state.appData?.locations ?? []);
  // row.buildLocationId is the SAP location id, matched against locSapId (not the internal locationId).
  const accessibleLocSapIds = useMemo(
    () => new Set(accessibleLocations.map((loc) => loc.locSapId)),
    [accessibleLocations]
  );
  const canConfirmReview = (row: Record<string, any>) =>
    roleId != null && REVIEW_CONFIRM_ROLE_IDS.has(roleId) && accessibleLocSapIds.has(row.buildLocationId);
  const checkTableWidth = useMemo(() => checkColumns.reduce((acc, col) => acc + (col.minWidth ?? 120), 0), []);
  const checkContentWidthStyle = useMemo(() => ({ width: checkTableWidth }), [checkTableWidth]);

  const updatePendingState = (rowKey: string, pending: boolean) => {
    setPendingBuildRowMap((current) => {
      if (pending) {
        return { ...current, [rowKey]: true };
      }
      const next = { ...current };
      delete next[rowKey];
      return next;
    });
  };

  const submitReviewToggle = async (row: Record<string, any>) => {
    const rowKey = getBuildRowKey(row);
    updatePendingState(rowKey, true);
    try {
      const res = await updateOrderRuleCheckReview(row.buildLocationId, row.materialNumber, row.salesLocationId);
      setAcceptedBuildRowMap((current) => ({ ...current, [rowKey]: res.data.reviewed === 'Y' }));
      setReviewMetaMap((current) => ({
        ...current,
        [rowKey]: { reviewedBy: res.data.reviewedBy, reviewedTime: res.data.reviewedTime },
      }));
      await onReviewToggleSuccess?.();
    } finally {
      updatePendingState(rowKey, false);
    }
  };

  const handleConfirmReviewed = (row: Record<string, any>) => submitReviewToggle(row);

  const handleUndoReviewed = (row: Record<string, any>) => submitReviewToggle(row);

  const toggleRow = (rowKey: string) => {
    setExpandedRowKeys((current) => ({ ...current, [rowKey]: !current[rowKey] }));
  };

  // By default only rows flagged for review are shown — OK (fully aligned) rows
  // do not render. Enabling "Show Only Reviewed" renders every row the API
  // returns (including OK rows) instead of filtering down to review-only.
  const filteredCheckBuildRows = useMemo(() => {
    return checkBuildRows.filter((row) => {
      if (!showOnlyReviewed && !isReviewRow(row)) return false;
      if (showOnlyNonInvoicedSkus && row.invoiced) return false;
      if (!showNonEligibleSkus && !row.eligible) return false;
      return true;
    });
  }, [checkBuildRows, showOnlyNonInvoicedSkus, showNonEligibleSkus, showOnlyReviewed]);

  // Rows arrive already sorted by the backend (sortBy/sortOrder in the API
  // request), so no client-side re-sort is applied here.
  const sortedCheckRows = filteredCheckBuildRows;

  // Count parent rows plus any currently-expanded cross dock child rows so the
  // table body can grow to fit them instead of hiding new rows behind a scroll.
  const totalVisibleRowCount = useMemo(
    () =>
      sortedCheckRows.reduce((count, row) => {
        const rowKey = getBuildRowKey(row);
        const crossdocks: Array<Record<string, any>> = row.crossdocks ?? [];
        const childRowCount = expandedRowKeys[rowKey] ? crossdocks.length : 0;
        return count + 1 + childRowCount;
      }, 0),
    [sortedCheckRows, expandedRowKeys]
  );

  const renderOrderRuleCheckFlagCell = (
    row: { eligible: boolean; invoiced: boolean },
    effectiveReviewFlag: boolean,
    effectiveFlagText: string,
  ) => (
    <View style={styles.orderRuleFlagWrap}>
      <View style={[styles.orderRuleFlagChip, effectiveReviewFlag ? styles.orderRuleFlagReview : styles.orderRuleFlagOk]}>
        <Text style={[styles.orderRuleFlagChipText, effectiveReviewFlag ? styles.orderRuleFlagReviewText : styles.orderRuleFlagOkText]}>
          {effectiveFlagText}
        </Text>
      </View>
      {!row.eligible ? (
        <View style={[styles.orderRuleFlagChip, styles.orderRuleFlagSecondaryChip]}>
          <Text style={[styles.orderRuleFlagChipText, styles.orderRuleFlagSecondaryText]}>Non-eligible</Text>
        </View>
      ) : null}
      {!row.invoiced ? (
        <View style={[styles.orderRuleFlagChip, styles.orderRuleFlagSecondaryChip]}>
          <Text style={[styles.orderRuleFlagChipText, styles.orderRuleFlagSecondaryText]}>Non-invoiced</Text>
        </View>
      ) : null}
    </View>
  );

  const isRowHoverable = Platform.OS === 'web';

  return (
    <View style={styles.sectionGap}>
      <View style={[styles.card, styles.orderRuleCheckToggleCard]}>
        <View style={styles.orderRuleCheckToggleRow}>
          <View style={styles.orderRuleCheckToggleItem}>
            <TouchableOpacity
              style={[
                styles.maxPotentialSwitchTrack,
                showOnlyNonInvoicedSkus && styles.maxPotentialSwitchTrackOn,
                showNonEligibleSkus && styles.orderRuleCheckToggleDisabled,
              ]}
              activeOpacity={0.85}
              disabled={showNonEligibleSkus}
              onPress={() => onShowOnlyNonInvoicedSkusChange(!showOnlyNonInvoicedSkus)}
            >
              <View style={[styles.maxPotentialSwitchThumb, showOnlyNonInvoicedSkus && styles.maxPotentialSwitchThumbOn]} />
            </TouchableOpacity>
            <Text style={[styles.orderRuleCheckToggleLabel, showNonEligibleSkus && styles.orderRuleCheckToggleLabelDisabled]}>
              Show only non-invoiced SKUs
            </Text>
            <DLOTooltip
              content="Non-invoiced SKUs have had no sales in the trailing 6 full weeks. Switch these to non-eligible so they stop generating order rule recommendations."
              trigger="hover"
              position="top"
              maxWidth={300}
            >
              <View style={styles.orderRuleCheckInfoBtn}>
                <MaterialIcons name="info-outline" size={14} color="#6B7280" />
              </View>
            </DLOTooltip>
          </View>

          <View style={styles.orderRuleCheckToggleItem}>
            <TouchableOpacity
              style={[
                styles.maxPotentialSwitchTrack,
                showNonEligibleSkus && styles.maxPotentialSwitchTrackOn,
                showOnlyNonInvoicedSkus && styles.orderRuleCheckToggleDisabled,
              ]}
              activeOpacity={0.85}
              disabled={showOnlyNonInvoicedSkus}
              onPress={() => onShowNonEligibleSkusChange(!showNonEligibleSkus)}
            >
              <View style={[styles.maxPotentialSwitchThumb, showNonEligibleSkus && styles.maxPotentialSwitchThumbOn]} />
            </TouchableOpacity>
            <Text style={[styles.orderRuleCheckToggleLabel, showOnlyNonInvoicedSkus && styles.orderRuleCheckToggleLabelDisabled]}>
              Show non-eligible SKUs
            </Text>
          </View>

          <View style={styles.orderRuleCheckToggleItem}>
            <TouchableOpacity
              style={[styles.maxPotentialSwitchTrack, showOnlyReviewed && styles.maxPotentialSwitchTrackOn]}
              activeOpacity={0.85}
              onPress={() => onShowOnlyReviewedChange(!showOnlyReviewed)}
            >
              <View style={[styles.maxPotentialSwitchThumb, showOnlyReviewed && styles.maxPotentialSwitchThumbOn]} />
            </TouchableOpacity>
            <Text style={styles.orderRuleCheckToggleLabel}>Show Only Reviewed</Text>
          </View>

          <Text style={styles.maxPotentialToggleDescription}>
            A flag is raised when Build Pallet, Pack Spec, and Order Rule quantities do not align for a build location
          </Text>
        </View>
      </View>

      <View style={styles.card}>
        <View style={styles.cardHeaderRow}>
          <View>
            <Text style={styles.cardTitle}>Order Rules Check</Text>
            <Text style={styles.cardSubTitle}>
              Cases-per-layer alignment across build pallet, pack spec, and order rule · only rows needing review are shown · expand a row to see its cross dock sites
            </Text>
          </View>
          <ExportButton testID="order-rules-export-check" onExport={onExport} />
        </View>

        {loading ? (
          <TableSkeleton rows={9} />
        ) : sortedCheckRows.length === 0 ? (
          <View style={styles.emptyStateWrap}>
            <Text style={styles.emptyStateText}>No records match the current selection.</Text>
          </View>
        ) : (
          // Single horizontal scroll wraps both header and the vertically-scrolling body
          // so they always share one width and scroll in lockstep — no separate sync needed.
          <View style={styles.orderRuleCheckTableClip}>
            <ScrollView horizontal showsHorizontalScrollIndicator bounces={false}>
              <View style={{ width: checkTableWidth }}>
                <View style={styles.tableHeaderRow}>
                  {checkColumns.map((column) => {
                    const isActive = checkSort.col === column.key;
                    // 'flag' and 'reviewAction' columns are not straightforward to sort — flag is composite, reviewAction is an action.
                    // We allow sorting on all columns for consistency; flag sorts by its text value.
                    return (
                      <TouchableOpacity
                        key={column.key}
                        style={[
                          styles.tableHeaderCell,
                          getColumnSizingStyle(column),
                          styles.tableHeaderCellAlignCenter,
                        ]}
                        onPress={() => handleCheckSort(column.key)}
                        activeOpacity={0.7}
                        accessibilityLabel={`Sort by ${column.label}`}
                      >
                        <View style={styles.maxPotentialHeaderTextWrap}>
                          <Text style={styles.tableHeaderText} numberOfLines={2}>{column.label}</Text>
                          {checkHeaderTooltips[column.key] ? (
                            <DLOTooltip content={checkHeaderTooltips[column.key]} trigger="hover" position="top" maxWidth={300}>
                              <View style={styles.maxPotentialHeaderInfoIcon}>
                                <MaterialIcons name="info-outline" size={12} color="#818181" />
                              </View>
                            </DLOTooltip>
                          ) : null}
                        </View>
                        <MaterialIcons
                          name={isActive ? (checkSort.dir === 'asc' ? 'keyboard-arrow-up' : 'keyboard-arrow-down') : 'unfold-more'}
                          size={14}
                          color={isActive ? '#005CBC' : '#9E9E9E'}
                          style={styles.tableHeaderSortIcon}
                        />
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {/* Body grows to fit expanded cross dock rows, only scrolling past MAX_VISIBLE_ROWS */}
                <View style={{ height: Math.min(totalVisibleRowCount, MAX_VISIBLE_ROWS) * ROW_HEIGHT, overflow: 'hidden' }}>
                  <ScrollView showsVerticalScrollIndicator>
              {sortedCheckRows.map((row, rowIndex) => {
                const rowKey = getBuildRowKey(row);
                const isHovered = hoveredRowKey === rowKey;
                const orderRuleValue = !row.eligible ? 'N/A' : String(row.orderRule ?? '-');
                const rowIsReview = isReviewRow(row);
                // OK rows surfaced via "Show Only Reviewed" are already accepted server-side,
                // so default them to Accepted unless the user has locally undone that row.
                const rowAccepted = rowKey in acceptedBuildRowMap ? acceptedBuildRowMap[rowKey] : (!rowIsReview && showOnlyReviewed);
                const rowPending = Boolean(pendingBuildRowMap[rowKey]);
                const effectiveReviewFlag = rowIsReview && !rowAccepted;
                const effectiveFlagText = effectiveReviewFlag ? 'Review' : 'OK';
                const showReviewAction = (rowIsReview || showOnlyReviewed) && canConfirmReview(row);
                const crossdocks: Array<Record<string, any>> = row.crossdocks ?? [];
                const isExpandable = crossdocks.length > 0;
                const isExpanded = isExpandable && !!expandedRowKeys[rowKey];

                return (
                  <React.Fragment key={`check-build-${rowIndex}`}>
                    <Pressable
                      style={[styles.tableDataRow, isHovered && styles.tableDataRowHover]}
                      onHoverIn={isRowHoverable ? () => setHoveredRowKey(rowKey) : undefined}
                      onHoverOut={isRowHoverable ? () => setHoveredRowKey(null) : undefined}
                    >
                      {checkColumns.map((column) => {
                        const value = row[column.key as keyof typeof row];
                        return (
                          <View
                            key={`${rowIndex}-${column.key}`}
                            style={[
                              styles.tableDataCell,
                              getColumnSizingStyle(column),
                              { overflow: 'hidden' },
                              styles.alignCenter,
                            ]}
                          >
                            {column.key === 'flag' ? (
                              renderOrderRuleCheckFlagCell(row as any, effectiveReviewFlag, effectiveFlagText)
                            ) : column.key === 'reviewAction' ? (
                              showReviewAction ? (
                                rowPending ? (
                                  <ActivityIndicator size="small" color="#005CBC" />
                                ) : rowAccepted ? (
                                  <View style={styles.orderRuleReviewActionWrap}>
                                    <View style={styles.orderRuleReviewActionRow}>
                                      <View style={styles.orderRuleReviewAcceptedBadge}>
                                        <Text style={styles.orderRuleReviewAcceptedBadgeText}>Accepted</Text>
                                      </View>
                                      <TouchableOpacity activeOpacity={0.8} onPress={() => handleUndoReviewed(row)}>
                                        <Text style={styles.orderRuleReviewUndoText}>Undo</Text>
                                      </TouchableOpacity>
                                    </View>
                                    {reviewMetaMap[rowKey]?.reviewedBy && reviewMetaMap[rowKey]?.reviewedTime ? (
                                      <Text style={styles.orderRuleReviewMetaText} numberOfLines={1}>
                                        {`By ${formatNameFromEmail(reviewMetaMap[rowKey].reviewedBy, undefined)} on ${formatReviewedDate(reviewMetaMap[rowKey].reviewedTime as string)}`}
                                      </Text>
                                    ) : null}
                                  </View>
                                ) : (
                                  <TouchableOpacity
                                    style={styles.orderRuleReviewActionBtn}
                                    activeOpacity={0.8}
                                    onPress={() => handleConfirmReviewed(row)}
                                  >
                                    <Text style={styles.orderRuleReviewActionBtnText}>Confirm reviewed</Text>
                                  </TouchableOpacity>
                                )
                              ) : (
                                <Text style={styles.tableMutedText}>-</Text>
                              )
                            ) : column.key === 'orderRule' ? (
                              <Text style={[styles.tableDataText, styles.tableDataTextStrong]} numberOfLines={1}>{orderRuleValue}</Text>
                            ) : column.key === 'location' ? (
                              <View style={styles.maxPotentialBuildLocationWrap}>
                                {isExpandable ? (
                                  <TouchableOpacity
                                    onPress={() => toggleRow(rowKey)}
                                    style={styles.maxPotentialRowToggleBtn}
                                    activeOpacity={0.8}
                                  >
                                    <MaterialIcons
                                      name="chevron-right"
                                      size={15}
                                      color="#5D6878"
                                      style={isExpanded ? styles.maxPotentialRowToggleIconOpen : null}
                                    />
                                  </TouchableOpacity>
                                ) : (
                                  // No cross dock (sales location) data for this material — omit the expand affordance entirely.
                                  <View style={styles.maxPotentialRowToggleBtn} />
                                )}
                                <TouchableOpacity activeOpacity={0.75} onPress={() => {}}>
                                  <Text style={styles.tableLinkText} numberOfLines={1}>{String(value ?? '-')}</Text>
                                </TouchableOpacity>
                              </View>
                            ) : (
                              <Text style={styles.tableDataText} numberOfLines={1}>{String(value ?? '-')}</Text>
                            )}
                          </View>
                        );
                      })}
                    </Pressable>

                    {isExpanded
                      ? crossdocks.map((xdRow, xdIndex) => (
                          <View key={`check-build-${rowIndex}-xd-${xdIndex}`} style={[styles.tableDataRow, styles.maxPotentialChildRow]}>
                            {checkColumns.map((column) => {
                              const xdOrderRuleValue = !xdRow.eligible ? 'N/A' : String(xdRow.orderRule ?? '-');
                              return (
                                <View
                                  key={`${rowIndex}-xd-${xdIndex}-${column.key}`}
                                  style={[
                                    styles.tableDataCell,
                                    getColumnSizingStyle(column),
                                    { overflow: 'hidden' },
                                    styles.alignCenter,
                                  ]}
                                >
                                  {column.key === 'location' ? (
                                    <View style={styles.maxPotentialChildLocationWrap}>
                                      <View style={styles.maxPotentialChildTag}>
                                        <Text style={styles.maxPotentialChildTagText}>Cross dock</Text>
                                      </View>
                                      <Text style={styles.tableDataText}>{xdRow.crossDockLocation}</Text>
                                    </View>
                                  ) : column.key === 'buildPallet' || column.key === 'packSpec' ? (
                                    <Text style={styles.tableMutedText}>-</Text>
                                  ) : column.key === 'orderRule' ? (
                                    <Text style={[styles.tableDataText, styles.tableDataTextStrong]} numberOfLines={1}>{xdOrderRuleValue}</Text>
                                  ) : column.key === 'flag' ? (
                                    renderOrderRuleCheckFlagCell(xdRow as any, Boolean(xdRow.reviewFlag), String(xdRow.flag ?? '-'))
                                  ) : column.key === 'reviewAction' ? (
                                    <Text style={styles.tableMutedText}>-</Text>
                                  ) : (
                                    <Text style={styles.tableDataText} numberOfLines={1}>{String(xdRow[column.key] ?? '-')}</Text>
                                  )}
                                </View>
                              );
                            })}
                          </View>
                        ))
                      : null}
                  </React.Fragment>
                );
              })}
                  </ScrollView>
                </View>
              </View>
            </ScrollView>
          </View>
        )}
        {!loading && sortedCheckRows.length > 0 ? pagination : null}
      </View>
    </View>
  );
};

export default OrderRuleCheckTab;
